import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SignatureDialog from "@/components/SignatureDialog";
import { ShieldCheck, Eye, PenLine, Check, Download, AlertCircle } from "lucide-react";

export default function SignPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [signKey, setSignKey] = useState(null);

  const load = useCallback(() => {
    api.get(`/sign/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail) || "This signing link is invalid or has expired."));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const renderUrl = (k, fmt) => `${API}/sign/${token}/${k}/render?fmt=${fmt}`;

  const doSign = async (signerName, type, dataUrl) => {
    try {
      await api.post(`/sign/${token}/${signKey}`, { signer_name: signerName, signature_type: type, signature_data: dataUrl });
      toast.success("Agreement signed — thank you!");
      setSignKey(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); throw e; }
  };

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 relative z-10" data-testid="sign-portal-error">
        <Card className="p-8 bg-card/60 border-border text-center max-w-sm">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <h1 className="font-display text-xl font-semibold">Link unavailable</h1>
          <p className="text-sm text-muted-foreground mt-2">{err}</p>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const signTitle = data.agreements.find((a) => a.atype === signKey)?.title;

  return (
    <div className="min-h-screen relative z-10 max-w-2xl mx-auto px-5 py-10">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center"><ShieldCheck className="h-6 w-6 text-white" /></div>
        <div>
          <div className="font-display font-bold text-lg leading-none">Urbanvision Financial</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">Client Signing Portal</div>
        </div>
      </div>

      <h1 className="font-display text-3xl font-bold tracking-tight">Welcome, {data.client_name}</h1>
      <p className="text-muted-foreground mt-2 mb-8 text-sm">Please review and sign the agreements below. You can preview each one before signing. Signed copies are saved securely.</p>

      <div className="space-y-3">
        {data.agreements.map((a) => {
          const signed = a.status === "signed";
          return (
            <Card key={a.atype} className="p-5 bg-card/60 border-border" data-testid={`sign-card-${a.atype}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold">{a.title}</div>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{a.description}</p>
                  <div className="mt-2">
                    {signed
                      ? <Badge className="bg-emerald-500/20 text-emerald-300" data-testid={`sign-status-${a.atype}`}><Check className="h-3 w-3 mr-1" /> Signed · {(a.signed_at || "").slice(0, 10)}</Badge>
                      : <Badge variant="secondary" className="text-muted-foreground" data-testid={`sign-status-${a.atype}`}>Awaiting signature</Badge>}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
                <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => window.open(renderUrl(a.atype, "html"), "_blank")} data-testid={`sign-preview-${a.atype}`}><Eye className="h-4 w-4 mr-1" /> Preview</Button>
                {!signed
                  ? <Button size="sm" className="rounded-lg" onClick={() => setSignKey(a.atype)} data-testid={`sign-now-${a.atype}`}><PenLine className="h-4 w-4 mr-1" /> Sign</Button>
                  : <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => window.open(renderUrl(a.atype, "pdf"), "_blank")} data-testid={`sign-pdf-${a.atype}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>}
              </div>
            </Card>
          );
        })}
      </div>

      <SignatureDialog open={!!signKey} onClose={() => setSignKey(null)} title={signTitle} onSubmit={doSign} />
    </div>
  );
}
